import { useValue } from "@legendapp/state/react";
import { Icon } from "@repo/ui/components/icon";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuTrigger,
} from "@repo/ui/ui/context-menu";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@repo/ui/ui/dropdown-menu";
import { useNavigate, useRouterState } from "@tanstack/react-router";
import type { ReactElement } from "react";

import { deleteThread, threads$, toggleThreadFavorite } from "~/stores/threads";

/**
 * Dual-mode options menu for a chat thread, mirroring {@link BoardOptions}.
 * Renders as a dropdown (from the ⋯ action / topbar button) or a right-click
 * context menu depending on `type`. Threads have no Share/Duplicate, but do
 * expose an explicit Rename since titles are auto-generated and double-click
 * is undiscoverable.
 */
export const ThreadOptions = ({
  threadId,
  children,
  type = "dropdown",
  onRename,
}: {
  threadId: string;
  children: ReactElement;
  type?: "dropdown" | "context";
  /**
   * Present in the sidebar (triggers inline rename); omitted in the topbar,
   * where the title is directly editable, so the Rename item is hidden.
   */
  onRename?: () => void;
}) => {
  const favorite = useValue(
    () => threads$[threadId].metadata.favorite.get() === true,
  );
  const navigate = useNavigate();
  const isViewingThread = useRouterState({
    select: (s) => s.location.pathname === `/thread/${threadId}`,
  });

  const Menu = type === "dropdown" ? DropdownMenu : ContextMenu;
  const MenuTrigger =
    type === "dropdown" ? DropdownMenuTrigger : ContextMenuTrigger;
  const MenuContent =
    type === "dropdown" ? DropdownMenuContent : ContextMenuContent;
  const MenuItem = type === "dropdown" ? DropdownMenuItem : ContextMenuItem;
  const Separator =
    type === "dropdown" ? DropdownMenuSeparator : ContextMenuSeparator;

  const handleDelete = () => {
    deleteThread(threadId);
    if (isViewingThread) navigate({ to: "/boards" });
  };

  return (
    <Menu>
      <MenuTrigger render={children} />
      <MenuContent align="end">
        <MenuItem onClick={() => toggleThreadFavorite(threadId)}>
          <Icon name={favorite ? "star-disabled" : "star"} />
          {favorite ? "Unfavorite" : "Favorite"}
        </MenuItem>
        {onRename && (
          <MenuItem onClick={onRename}>
            <Icon name="pencil" />
            Rename
          </MenuItem>
        )}
        <Separator />
        <MenuItem variant="destructive" onClick={handleDelete}>
          <Icon name="trash" />
          Delete thread
        </MenuItem>
      </MenuContent>
    </Menu>
  );
};
